// The code in this file is intended to be temporary as we roll out the new system
// once all v1 files are gone, this will be deleted
import type { SimulationUpload, SimulationUploadErrorType, SimulationServiceVersion } from '~/store/simulation/types';
import type {
  SimulationServiceV1SimulationFileUploadsDetailData,
  SimulationServiceV1SimulationFileUploadsListData,
} from '@shipium-corp/shipium-ts-types';

export type UploadSource = { type: 'legacy' } | { type: 'new'; fileUploadId: string };

export interface UnifiedSimulationUpload extends SimulationUpload {
  _uploadSource?: UploadSource;
  _visibleToPartner?: boolean;
  // simulation-service stores setup data as an opaque document and hands the marker back with the
  // leading underscore dropped and the first letter capitalised (`_visibleToPartner` comes back the
  // same way). A selection read back from a saved simulation carries only this spelling.
  UploadSource?: UploadSource;
}

type FileUploadListItem = SimulationServiceV1SimulationFileUploadsListData[number];

function mapStatus(status: string): { processed: boolean; errorType?: SimulationUploadErrorType } {
  switch (status) {
    case 'valid':
    case 'processed':
      return { processed: true };
    case 'invalid':
      return { processed: true, errorType: 'invalid_file' };
    case 'pending':
    case 'validating':
    default:
      return { processed: false };
  }
}

export function adaptFileUploadListItem(item: FileUploadListItem): UnifiedSimulationUpload {
  const { processed, errorType } = mapStatus(item.status);

  return {
    s3Bucket: item.s3Bucket,
    s3Key: item.s3Key,
    originalFileName: item.originalFileName,
    uploadType: 'simulation',
    userId: item.createdBy,
    partnerId: item.partnerId,
    lastFileModificationTimestamp: item.updatedAt,
    mimeType: item.mimeType ?? 'text/csv',
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    details: {
      processed,
      includesHistorical: item.includesHistorical ?? false,
      shipmentCount: item.totalRows ?? undefined,
      // shipped-date range for the table's Start/End Date columns (null on older API responses)
      ...(item.startDate ? { minShipDate: item.startDate } : {}),
      ...(item.endDate ? { maxShipDate: item.endDate } : {}),
      ...(errorType ? { errorType } : {}),
    },
    _uploadSource: { type: 'new', fileUploadId: item.id },
    _visibleToPartner: item.visibleToPartner,
  };
}

export function enrichWithFileUploadDetail(
  upload: UnifiedSimulationUpload,
  detail: SimulationServiceV1SimulationFileUploadsDetailData,
): UnifiedSimulationUpload {
  const minShipDate = detail.temporalMetadata?.shippedDateTime?.min;
  const maxShipDate = detail.temporalMetadata?.shippedDateTime?.max;

  const origins = detail.geographyMetadata?.origins
    ? Object.entries(detail.geographyMetadata.origins).reduce(
        (acc, [postalCode, count]) => {
          acc[postalCode] = {
            originPostalCode: postalCode,
            minShipDate: minShipDate ?? '',
            maxShipDate: maxShipDate ?? '',
            shipmentCount: count,
          };
          return acc;
        },
        {} as Record<
          string,
          { originPostalCode: string; minShipDate: string; maxShipDate: string; shipmentCount: number }
        >,
      )
    : undefined;

  const fulfillmentContexts = detail.shipmentMetadata?.fulfillmentContexts
    ? Object.entries(detail.shipmentMetadata.fulfillmentContexts).reduce(
        (acc, [contextId, count]) => {
          acc[contextId] = {
            minShipDate: minShipDate ?? '',
            maxShipDate: maxShipDate ?? '',
            shipmentCount: count,
          };
          return acc;
        },
        {} as Record<string, { minShipDate: string; maxShipDate: string; shipmentCount: number }>,
      )
    : undefined;

  return {
    ...upload,
    details: {
      ...upload.details!,
      minShipDate,
      maxShipDate,
      ...(origins ? { origins } : {}),
      ...(fulfillmentContexts ? { fulfillmentContexts } : {}),
    },
  };
}

// True when the per-origin / per-fulfillment-context breakdown is missing and can be fetched back.
// Shared by the file picker and the mapping step so the two cannot disagree on when to re-read.
export const needsFileUploadDetail = (upload: UnifiedSimulationUpload): boolean =>
  !!getFileUploadId(upload) && !upload.details?.origins && !upload.details?.fulfillmentContexts;

// `details.origins` / `details.fulfillmentContexts` hold one entry per origin postal code and per
// fulfillment context, hundreds of KB per file at partner scale. simulationSetupData is re-PUT
// whole at every wizard step, so whatever is persisted here counts against the simulation API's
// 1 MiB body limit. Anything carrying a fileUploadId is re-derivable from the file-uploads detail
// endpoint and is dropped before persisting; legacy uploads have no detail endpoint to re-read and
// keep what they carry.
function toPersistableUpload(upload: UnifiedSimulationUpload): UnifiedSimulationUpload {
  if (!getFileUploadId(upload) || !upload.details) return upload;
  const { origins, fulfillmentContexts, ...details } = upload.details;
  return { ...upload, details };
}

// v1 only: starting a guided simulation rebuilds its origin postal codes synchronously from the
// persisted selection (getPayloadForSimulationType → generateOriginRecordsFromUpload), so stripping
// there would silently start the run with no postal codes. v2 start takes no payload.
export const toPersistableSelection = (
  uploads: UnifiedSimulationUpload[],
  version: SimulationServiceVersion,
): UnifiedSimulationUpload[] => (version === 'v2' ? uploads.map(toPersistableUpload) : uploads);

// Both spellings, because the same selection is read from component state mid-wizard and from a
// persisted simulation on resume, and only the round trip renames the key.
const uploadSourceOf = (upload: UnifiedSimulationUpload): UploadSource | undefined =>
  upload._uploadSource ?? upload.UploadSource;

export function isNewUpload(upload: UnifiedSimulationUpload): boolean {
  return uploadSourceOf(upload)?.type === 'new';
}

export function getFileUploadId(upload: UnifiedSimulationUpload): string | undefined {
  const source = uploadSourceOf(upload);
  return source?.type === 'new' ? source.fileUploadId : undefined;
}

// historicalShipmentS3Locations value: v2 batch generation needs a full `s3://bucket/key` URI (a
// bare key 400s "Invalid source location"); v1 stores the bare key.
export function toSimulationSourceLocation(
  upload: Pick<SimulationUpload, 's3Bucket' | 's3Key'>,
  version: SimulationServiceVersion,
): string {
  return version === 'v2' ? `s3://${upload.s3Bucket}/${upload.s3Key}` : upload.s3Key;
}

// Inverse of toSimulationSourceLocation for the gateway's /getPresignedDownloadUrl, which signs
// `${sessionPartnerId}/${subKey}` against its own bucket. Accepts either stored form (v2
// `s3://bucket/<partnerId>/...` or v1 bare `<partnerId>/...`) and returns the key with the bucket
// and partner ID segment removed. The URI's bucket is discarded: the gateway does not take one.
export function toPresignedDownloadSubKey(location: string | null | undefined): string {
  if (!location) return '';
  const key = location.startsWith('s3://') ? location.slice('s3://'.length).split('/').slice(1).join('/') : location;
  return key.split('/').slice(1).join('/');
}
